import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Carte,
  Chargement,
  Compteur,
  EnteteEcran,
  LigneAlerte,
  MAX_ALERTES_TABLEAU_BORD,
  MessageErreur,
  Pastille,
  PastilleSite,
  PiedAlertes,
} from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { usePerimetre } from '../site/usePerimetre';
import { TOUS_LES_SITES } from '../site/context';
import { useAlertes } from '../lib/useAlertes';
import { useDashboardFacturation } from '../lib/useDashboardFacturation';

const ACCES_RAPIDES = [
  {
    libelle: 'Financeurs & OPCO',
    description: 'Blocs Financement, échéances, accords OF',
    ecran: 'financing',
  },
  {
    libelle: 'Factures',
    description: 'Factures et avoirs, envoi, règlements, export',
    ecran: 'invoicing',
  },
  {
    libelle: 'Paramètres de facturation',
    description: 'Numérotation, émetteur, certificats',
    ecran: 'fac-parametres',
  },
  {
    libelle: 'Formateurs',
    description: 'Pièces, factures et heures déclarées',
    ecran: 'instructors',
  },
];

function pluriel(nombre: number, singulier: string, plurielTexte = `${singulier}s`): string {
  return `${nombre} ${nombre > 1 ? plurielTexte : singulier}`;
}

/**
 * Tableau de bord du pôle facturation (TF-424). Compteurs et tâches viennent de
 * `GET /api/dashboard/facturation`, qui reprend les lectures de « Financeurs &
 * OPCO » et de « Factures » : rien n'est compté ni complété à l'écran. Chaque
 * tâche mène là où elle se traite — le bloc Financement du dossier pour
 * facturer et émettre, la fiche de la facture pour l'envoi et le règlement.
 */
export function DashboardFacturationPage(): JSX.Element {
  const navigate = useNavigate();
  const { peutAcceder } = useAuth();
  const { site } = usePerimetre();
  const tous = !site || site === TOUS_LES_SITES;
  const { data, isLoading, error } = useDashboardFacturation(site);
  const { data: alertesData } = useAlertes(tous ? '' : site, 1, 25, 'OUVERTE');
  const alertes = alertesData?.dernieres ?? [];

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Pôle facturation"
        sousTitre="Facturation des financeurs · échéances, factures et avoirs, certificats, règlements"
        actions={
          <>
            <BoutonSecondaire onClick={() => navigate('/invoicing')}>Factures</BoutonSecondaire>
            <BoutonPrincipal onClick={() => navigate('/financing')}>Financeurs & OPCO</BoutonPrincipal>
          </>
        }
      />

      <BarreSites />

      {isLoading && <Chargement libelle="Chargement du tableau de bord…" />}
      {!isLoading && (error || !data) && (
        <MessageErreur message="Impossible de charger le tableau de bord." />
      )}

      {data && (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Compteur
              libelle="À facturer"
              valeur={data.compteurs.aFacturer.nombre}
              detail={data.compteurs.aFacturer.detail}
              ton="warm"
            />
            <Compteur
              libelle="À émettre"
              valeur={data.compteurs.aEmettre}
              detail="brouillons préparés"
              ton="blue"
            />
            <Compteur
              libelle="À envoyer"
              valeur={data.compteurs.aEnvoyer}
              detail="émises, envoi au client non noté"
              ton="navy"
            />
            <Compteur
              libelle="En retard"
              valeur={data.compteurs.enRetard.nombre}
              detail={
                data.compteurs.resteDu.nombre === 0
                  ? 'aucune facture non réglée'
                  : `${data.compteurs.enRetard.texte} · reste dû ${data.compteurs.resteDu.texte} sur ${pluriel(
                      data.compteurs.resteDu.nombre,
                      'facture',
                    )}`
              }
              ton="red"
            />
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="min-w-0 space-y-6 lg:col-span-2">
              <Carte titre={`À traiter${tous ? ' · toutes antennes' : ` · ${site}`}`}>
                {data.taches.length === 0 ? (
                  <div className="flex items-center gap-4 px-4 py-4 sm:px-6">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-codialis-navy">
                        Rien à traiter sur ce périmètre
                      </div>
                      <div className="text-[11px] text-codialis-gray">
                        Aucune échéance à facturer, aucune pièce à émettre, à envoyer ou en retard
                      </div>
                    </div>
                    <Pastille texte="À jour" ton="success" />
                  </div>
                ) : (
                  data.taches.map((tache) => (
                    <button
                      key={tache.id}
                      type="button"
                      onClick={() => navigate(tache.page)}
                      className="flex w-full items-center gap-3 px-4 py-4 text-left transition hover:bg-gray-50 sm:gap-4 sm:px-6"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-codialis-navy">{tache.titre}</div>
                        <div className="text-[11px] text-codialis-gray">{tache.sousTitre}</div>
                      </div>
                      {tache.site && <PastilleSite site={tache.site} />}
                      <Pastille texte={tache.statut} ton={tache.ton} />
                    </button>
                  ))
                )}
              </Carte>

              <Carte titre="Accès rapides" sansBordure>
                <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 sm:p-6">
                  {ACCES_RAPIDES.filter((item) => peutAcceder(item.ecran)).map((item) => (
                    <button
                      key={item.ecran}
                      type="button"
                      onClick={() => navigate(`/${item.ecran}`)}
                      className="rounded-xl bg-gray-50 p-4 text-left transition hover:bg-gray-100"
                    >
                      <div className="text-sm font-semibold text-codialis-navy">{item.libelle}</div>
                      <div className="mt-0.5 text-[11px] text-codialis-gray">{item.description}</div>
                    </button>
                  ))}
                </div>
              </Carte>
            </div>

            <div className="min-w-0 space-y-6">
              <Carte
                titre="Mes alertes"
                actions={
                  <BoutonSecondaire onClick={() => navigate('/alertes')}>Module d'alertes</BoutonSecondaire>
                }
              >
                {alertes.length === 0 ? (
                  <LigneAlerte titre="Aucune alerte ouverte" detail="Rien à signaler sur ce périmètre" />
                ) : (
                  alertes
                    .slice(0, MAX_ALERTES_TABLEAU_BORD)
                    .map((alerte) => (
                      <LigneAlerte
                        key={alerte.id}
                        titre={alerte.libelleRegle}
                        detail={alerte.message}
                        ton={alerte.regle === 'FACTURE_EN_RETARD' ? 'danger' : 'warning'}
                        site={alerte.siteCode ?? undefined}
                      />
                    ))
                )}
                <PiedAlertes
                  restantes={(alertesData?.pagination.total ?? 0) - MAX_ALERTES_TABLEAU_BORD}
                  onClick={() => navigate('/alertes')}
                />
              </Carte>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
